import React from 'react';
import { Link } from 'react-router-dom';
import { FiDownload, FiInfo } from 'react-icons/fi';

export default function SizeGuide() {
  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
            Size Guide
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Find your perfect fit with our standard measurement chart. Download the complete guide for detailed sizing information.
          </p>
        </div>

        {/* Size Chart Image */}
        <div className="bg-gray-900 rounded-2xl p-8 mb-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Standard Measurement Chart</h2>
            <p className="text-gray-400">
              Use this chart to find your perfect size. Measure yourself and match to the corresponding size.
            </p>
          </div>
          
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-xl p-4 shadow-2xl">
              <img
                src="https://cdnbailebelle.pages.dev/size-guide/Standard Measurement Chart.jpg"
                alt="Standard Measurement Chart"
                className="max-w-full h-auto rounded-lg"
                loading="lazy"
                decoding="async"
                onLoad={(e) => {
                  e.target.style.opacity = '1';
                }}
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
                style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
              />
            </div>
          </div>

          {/* Download Button */}
          <div className="text-center">
            <a
              href="https://cdnbailebelle.pages.dev/size-guide/Standard Measurement Chart.docx"
              download="Standard Measurement Chart.docx"
              className="inline-flex items-center px-8 py-4 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600 transition-all duration-300 shadow-lg hover:shadow-pink-500/30 transform hover:scale-105"
            >
              <FiDownload className="mr-3" size={20} />
              Download Complete Size Guide (Word Document)
            </a>
            <p className="text-gray-400 text-sm mt-4">
              Download the detailed measurement chart for offline reference
            </p>
          </div>
        </div>

        {/* How to Measure Instructions */}
        <div className="bg-gray-900 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <FiInfo className="mr-3 text-pink-500" size={24} />
            How to Measure Yourself
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-pink-500 font-bold text-xl">1</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Bust</h3>
              <p className="text-gray-400 text-sm">
                Measure around the fullest part of your chest, keeping the tape measure parallel to the ground.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-pink-500 font-bold text-xl">2</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Waist</h3>
              <p className="text-gray-400 text-sm">
                Measure around the narrowest part of your waist, usually just above the belly button.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-pink-500 font-bold text-xl">3</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Hip</h3>
              <p className="text-gray-400 text-sm">
                Measure around the fullest part of your hips, about 7-9 inches below your waist.
              </p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Measurement Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Before You Measure:</h3>
              <ul className="text-gray-400 space-y-2">
                <li>• Wear form-fitting clothing or just undergarments</li>
                <li>• Use a flexible measuring tape</li>
                <li>• Stand straight with arms at your sides</li>
                <li>• Have someone help you for accurate measurements</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Measurement Tips:</h3>
              <ul className="text-gray-400 space-y-2">
                <li>• Keep the tape measure snug but not tight</li>
                <li>• Measure at the same time of day for consistency</li>
                <li>• Take measurements in inches for accuracy</li>
                <li>• Round to the nearest quarter inch</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-gray-900 rounded-2xl p-8">
          <div className="text-center">
            <FiInfo className="mx-auto text-pink-500 mb-4" size={48} />
            <h2 className="text-2xl font-bold text-white mb-4">Need Help Finding Your Size?</h2>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              If you're still unsure about your size after using our measurement chart, our customer service team is here to help. 
              Contact us with your measurements and we'll recommend the perfect size for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
